import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Camera,
  X,
  Home,
  Package,
  ShoppingBag,
  Truck,
  Store as StoreIcon,
  Ruler,
  ChevronDown,
  ChevronUp,
  Heart,
  Link as LinkIcon,
  ClipboardPaste,
  Crop,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { PhotoCropper } from '@/components/PhotoCropper';
import {
  Page,
  Main,
  Button,
  Field,
  inputClass,
  LoadingState,
  ErrorState,
} from '@/components/ui';
import { PickerSheet, PickerRow } from './components/PickerSheet';
import { ComboSelect } from './components/ComboSelect';
import api from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { downscaleToBase64, remoteImageToBase64 } from '@/lib/image';
import { bestStoreForDesigner, bestDesignerForStore } from '@/lib/match';
import { todayISO, cn } from '@/lib/utils';
import {
  CLOTHING_CATEGORIES,
  ACCESSORY_CATEGORIES,
  BAG_SUBTYPES,
  MATERIALS,
  COLORS,
  CARE_INSTRUCTIONS,
  SEASONS,
  CURRENCIES,
  toStoredSizeType,
  formatStoredSize,
  parseStoredSize,
} from '@/lib/constants';
import { SizeField } from './components/SizeField';
import type {
  Closet,
  Designer,
  ColorEntry,
  Item,
  ItemStatus,
  PendingType,
  Season,
  SizeSystem,
  Store,
} from '@/lib/types';

type FormStatus = Extract<ItemStatus, 'in-closet' | 'pending-delivery' | 'wishlist'>;

interface PhotoEntry {
  base64: string; // data URL preview / new upload
  existingId?: string; // present for photos already on the item (edit mode)
  kind?: 'item' | 'wear'; // garment-alone shot vs worn shot
}

interface CameraHandoff {
  photo?: string;
  photoKind?: 'item' | 'wear';
  // When set, the camera is in Rapid Intake — after saving, return to the camera
  // instead of the item detail page so the user can keep adding back-to-back.
  rapidIntake?: boolean;
  intakeCount?: number; // running tally to carry back to the camera
  prefill?: {
    name?: string;
    brand?: string;
    category?: string;
    color?: string;
    material?: string;
    season?: string;
    storeName?: string;
    storeId?: string;
    sizeSystem?: SizeSystem;
    size?: string;
    isBespoke?: boolean;
    bespokeDate?: string;
  };
}

export function WardrobeAddPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEdit = Boolean(editId);

  const { data: closetData } = useApi<{ closets: Closet[] }>('/closets');
  const closets = useMemo(() => closetData?.closets ?? [], [closetData]);

  const { data: designerData } = useApi<{ designers: Designer[] }>('/designers');
  const designerNames = useMemo(
    () => (designerData?.designers ?? []).map((d) => d.name),
    [designerData],
  );

  const { data: storeData, refetch: refetchStores } = useApi<{ stores: Store[] }>('/stores');
  const stores = useMemo(() => storeData?.stores ?? [], [storeData]);
  const storeNames = useMemo(() => stores.map((s) => s.name), [stores]);

  const storeForDesigner = (designerName: string): Store | null =>
    bestStoreForDesigner(stores, designerName);
  const designerForStore = (store: Store): string | null => bestDesignerForStore(store);

  const { data: editData, loading: editLoading, error: editError } = useApi<{ item: Item }>(
    editId ? `/items/${editId}` : null,
  );

  // ---- form state ----
  const [photos, setPhotos] = useState<PhotoEntry[]>([]);
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [dimLength, setDimLength] = useState('');
  const [dimWidth, setDimWidth] = useState('');
  const [dimHeight, setDimHeight] = useState('');
  const [dimUnit, setDimUnit] = useState<'cm' | 'in'>('cm');
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [cost, setCost] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [storeName, setStoreName] = useState('');
  const [storeId, setStoreId] = useState('');
  const [acquisitionDate, setAcquisitionDate] = useState(todayISO());
  const [deliveredDate, setDeliveredDate] = useState('');
  const [wishlistAddedDate, setWishlistAddedDate] = useState(todayISO());
  const [materials, setMaterials] = useState<string[]>([]);
  const [sizeSystem, setSizeSystem] = useState<SizeSystem>('relative');
  const [size, setSize] = useState('');
  const [isBespoke, setIsBespoke] = useState(false);
  const [bespokeDate, setBespokeDate] = useState('');
  const [mill, setMill] = useState('');
  const [colors, setColors] = useState<ColorEntry[]>([]);
  const [careInstructions, setCareInstructions] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<FormStatus>('in-closet');
  const [closetId, setClosetId] = useState('');
  const [onlineStoreUrl, setOnlineStoreUrl] = useState('');

  // Wishlist link-preview (best-effort)
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState(false);
  const [linkImage, setLinkImage] = useState<string | null>(null);

  // Clipboard paste hint
  const [pasteHint, setPasteHint] = useState<string | null>(null);

  // Crop editor target (index into photos), or null when closed.
  const [cropIndex, setCropIndex] = useState<number | null>(null);

  // Pending fields
  const [pendingType, setPendingType] = useState<PendingType>('being-delivered');
  const [carrier, setCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentLocation, setAppointmentLocation] = useState('');

  // Picker visibility
  const [showSubtype, setShowSubtype] = useState(false);
  const [showColor, setShowColor] = useState(false);
  const [showCare, setShowCare] = useState(false);
  const [showCloset, setShowCloset] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isWishlist = status === 'wishlist';

  // Default closet to main once loaded (create mode)
  useEffect(() => {
    if (!isEdit && closets.length && !closetId) {
      setClosetId(closets.find((c) => c.isMain)?.id ?? closets[0].id);
    }
  }, [closets, closetId, isEdit]);

  // Prefill from item in edit mode
  useEffect(() => {
    const item = editData?.item;
    if (!item) return;
    setName(item.name);
    setBrand(item.brand ?? '');
    setCategory(item.category);
    setSubcategory(item.subcategory ?? '');
    setDimLength(item.dimLength != null ? String(item.dimLength) : '');
    setDimWidth(item.dimWidth != null ? String(item.dimWidth) : '');
    setDimHeight(item.dimHeight != null ? String(item.dimHeight) : '');
    setDimUnit(item.dimUnit ?? 'cm');
    setSeasons(item.seasons ?? []);
    setCost(item.cost != null ? String(item.cost) : '');
    setCurrency(item.currency ?? 'USD');
    setStoreName(item.storeName ?? '');
    setStoreId(item.storeId ?? '');
    setAcquisitionDate(item.acquisitionDate ?? todayISO());
    setDeliveredDate(item.deliveredDate ?? '');
    setWishlistAddedDate(item.wishlistAddedDate ?? todayISO());
    setMaterials(item.materials ?? []);
    {
      const parsed = parseStoredSize(item.sizeType, item.size, item.category);
      setSizeSystem(parsed.system);
      setSize(parsed.value);
    }
    setIsBespoke(item.isBespoke);
    setBespokeDate(item.bespokeDate ?? '');
    setMill(item.mill ?? '');
    setColors(
      item.colors && item.colors.length
        ? item.colors
        : item.color
          ? [
              {
                name: item.color,
                hex: item.colorHex ?? COLORS.find((c) => c.name === item.color)?.hex ?? '#cccccc',
              },
            ]
          : [],
    );
    setCareInstructions(item.careInstructions ?? []);
    setDescription(item.description ?? '');
    setNotes(item.notes ?? '');
    if (item.status === 'in-closet' || item.status === 'pending-delivery' || item.status === 'wishlist') {
      setStatus(item.status);
    }
    setClosetId(item.closetId ?? '');
    setOnlineStoreUrl(item.onlineStoreUrl ?? '');
    setPendingType(item.pendingType ?? 'being-delivered');
    setCarrier(item.carrier ?? '');
    setTrackingNumber(item.trackingNumber ?? '');
    setExpectedDeliveryDate(item.expectedDeliveryDate ?? '');
    setAppointmentDate(item.appointmentDate ?? '');
    setAppointmentTime(item.appointmentTime ?? '');
    setAppointmentLocation(item.appointmentLocation ?? '');
    setPhotos(
      (item.photos ?? []).map((p) => ({ base64: p.url, existingId: p.id })),
    );
  }, [editData]);

  // Prefill a brand-new item from a camera capture (photo + optional AI details),
  // so the photo and any AI-detected fields persist into manual editing.
  useEffect(() => {
    if (editId) return;
    const s = location.state as CameraHandoff | null;
    if (!s) return;
    if (s.photo) setPhotos([{ base64: s.photo, kind: s.photoKind === 'wear' ? 'wear' : 'item' }]);
    const p = s.prefill;
    if (p) {
      if (p.name) setName(p.name);
      if (p.brand) setBrand(p.brand);
      if (p.category) setCategory(p.category);
      if (p.color) {
        setColors([{ name: p.color, hex: COLORS.find((c) => c.name === p.color)?.hex ?? '#cccccc' }]);
      }
      if (p.material) setMaterials([p.material]);
      if (p.season && (['spring', 'summer', 'fall', 'winter'] as string[]).includes(p.season)) {
        setSeasons([p.season as Season]);
      }
      if (p.storeName) setStoreName(p.storeName);
      if (p.storeId) setStoreId(p.storeId);
      if (p.sizeSystem) setSizeSystem(p.sizeSystem);
      if (p.size) setSize(p.size);
      if (p.isBespoke) {
        setIsBespoke(true);
        if (p.bespokeDate) setBespokeDate(p.bespokeDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle =<T,>(value: T, list: T[], set: (v: T[]) => void) => {
    set(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  };

  const isBags = category === 'Bags';

  // ---- materials multi-select (one <select> per chosen material) ----
  const addMaterial = () => setMaterials((prev) => [...prev, '']);
  const setMaterialAt = (index: number, value: string) =>
    setMaterials((prev) => prev.map((m, i) => (i === index ? value : m)));
  const removeMaterialAt = (index: number) =>
    setMaterials((prev) => prev.filter((_, i) => i !== index));
  const moveMaterial = (index: number, dir: -1 | 1) =>
    setMaterials((prev) => {
      const j = index + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });

  // ---- colors (ordered; colors[0] is the dominant color) ----
  const addColor = (c: ColorEntry) =>
    setColors((prev) => (prev.some((x) => x.name === c.name) ? prev : [...prev, c]));
  const removeColorAt = (index: number) =>
    setColors((prev) => prev.filter((_, i) => i !== index));
  const moveColor = (index: number, dir: -1 | 1) =>
    setColors((prev) => {
      const j = index + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });

  // Resolve a store name to an id, creating it if the user typed a new one.
  // Returns { storeId, storeName } to attach to the item.
  const resolveStore = async (): Promise<{ storeId: string | null; storeName: string | null }> => {
    const trimmed = storeName.trim();
    if (!trimmed) return { storeId: null, storeName: null };
    // Already linked to a known store id whose name still matches.
    const matched = stores.find((s) => s.id === storeId && s.name === trimmed)
      ?? stores.find((s) => s.name.toLowerCase() === trimmed.toLowerCase());
    if (matched) return { storeId: matched.id, storeName: matched.name };
    // New store — create it (ignore failures, still snapshot the name).
    try {
      const res = await api.post<{ store: Store }>('/stores', { name: trimmed });
      if (res?.store?.id) {
        refetchStores();
        return { storeId: res.store.id, storeName: res.store.name };
      }
    } catch {
      /* best-effort; fall through to name-only snapshot */
    }
    return { storeId: null, storeName: trimmed };
  };

  // Best-effort wishlist link preview. Never throws.
  const fetchLinkPreview = async () => {
    const url = onlineStoreUrl.trim();
    if (!url) return;
    setLinkLoading(true);
    setLinkError(false);
    try {
      const res = await api.get<{
        image?: string | null;
        imageData?: string | null;
        title?: string | null;
        price?: number | null;
        domain?: string | null;
        store?: { id: string; name: string } | null;
      }>(`/link-preview?url=${encodeURIComponent(url)}`);
      // Persistable image, in order of preference:
      //  1. server-fetched base64 (imageData)
      //  2. client-side canvas capture of the remote image (works for
      //     CORS-enabled CDNs like Shopify — the browser can already read it)
      //  3. the remote URL for display only (server retries on save)
      if (res?.imageData) {
        setLinkImage(res.imageData);
      } else if (res?.image) {
        const captured = await remoteImageToBase64(res.image);
        setLinkImage(captured ?? res.image);
      }
      if (res?.price != null && !cost) setCost(String(res.price));
      if (res?.store?.name) {
        setStoreName(res.store.name);
        setStoreId(res.store.id);
      } else if (res?.domain && !storeName.trim()) {
        setStoreName(res.domain);
        setStoreId('');
      }
    } catch {
      setLinkError(true);
    } finally {
      setLinkLoading(false);
    }
  };

  // Classify a photo as an item shot (garment alone) or a wear shot (worn).
  // Best-effort AI; defaults to 'item'. The user can override per photo.
  const detectKind = async (base64: string): Promise<'item' | 'wear'> => {
    try {
      const res = await api.post<{ detectedType: string }>('/ai/detect', { image: base64 });
      return res.detectedType === 'fit' ? 'wear' : 'item';
    } catch {
      return 'item';
    }
  };

  // Add photos. In EDIT mode they're uploaded immediately (and tracked with the
  // returned id) so adds/deletes persist without needing a full save. In CREATE
  // mode they stay local and are sent with POST /items on save. Each photo's
  // shot type is auto-detected (overridable via the Item/Wear toggle).
  const addPhotos = async (base64List: string[], forcedKind?: 'item' | 'wear') => {
    if (base64List.length === 0) return;
    for (const base64 of base64List) {
      const kind = forcedKind ?? (await detectKind(base64));
      if (isEdit && editId) {
        try {
          const res = await api.post<{ id: string }>(`/items/${editId}/photos`, {
            image: base64,
            isPrimary: photos.length === 0,
            kind,
          });
          setPhotos((prev) => [...prev, { base64, existingId: res.id, kind }]);
        } catch {
          setSubmitError('Could not upload that photo. Please try again.');
        }
      } else {
        setPhotos((prev) => [...prev, { base64, kind }]);
      }
    }
  };

  // Toggle a photo's shot type (persists immediately in edit mode).
  const setPhotoKind = async (index: number, kind: 'item' | 'wear') => {
    const entry = photos[index];
    setPhotos((prev) => prev.map((p, i) => (i === index ? { ...p, kind } : p)));
    if (isEdit && entry?.existingId) {
      try {
        await api.patch(`/photos/${entry.existingId}`, { kind });
      } catch {
        /* best-effort */
      }
    }
  };

  const handlePhotoFiles = async (files: FileList | null) => {
    if (!files) return;
    const base64List: string[] = [];
    for (const file of Array.from(files)) {
      try {
        base64List.push(await downscaleToBase64(file));
      } catch {
        // skip files that fail to encode
      }
    }
    await addPhotos(base64List);
  };

  const addImageBlob = async (blob: Blob): Promise<boolean> => {
    try {
      await addPhotos([await downscaleToBase64(blob)]);
      return true;
    } catch {
      return false;
    }
  };

  // Handle a paste anywhere on the page: pull image bits straight from the
  // clipboard (e.g. right-click → Copy Image on any site), or fetch a pasted
  // image URL. This is the most reliable way to grab images that block fetching.
  const handlePasteImage = async (clipboard: DataTransfer | null): Promise<boolean> => {
    if (!clipboard) return false;
    // 1. Pasted image data.
    for (const item of Array.from(clipboard.items)) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file && (await addImageBlob(file))) return true;
      }
    }
    // 2. Pasted image URL (best-effort, browser-side capture).
    const text = clipboard.getData('text/plain').trim();
    if (/^https?:\/\/\S+/i.test(text)) {
      const captured = await remoteImageToBase64(text);
      if (captured) {
        await addPhotos([captured]);
        return true;
      }
    }
    return false;
  };

  // Global paste listener while the form is open. Only acts when the clipboard
  // actually holds an image, so pasting text into inputs still works normally.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const cd = e.clipboardData;
      if (!cd) return;
      const hasImage = Array.from(cd.items).some((i) => i.type.startsWith('image/'));
      if (!hasImage) return; // let normal text paste proceed
      e.preventDefault();
      void handlePasteImage(cd);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Paste image" button: reads the clipboard via the async Clipboard API
  // (works without a focused field). Falls back silently if unsupported/denied.
  const pasteFromClipboardButton = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find((t) => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          if (await addImageBlob(blob)) return;
        }
      }
      setPasteHint('No image found in clipboard — copy an image first, then paste.');
    } catch {
      setPasteHint('Press Ctrl/Cmd+V to paste a copied image.');
    }
  };

  const removePhoto = async (index: number) => {
    const entry = photos[index];
    // In edit mode every photo is already persisted (existingId) — delete it
    // server-side immediately. In create mode photos are local-only.
    if (isEdit && entry?.existingId) {
      try {
        await api.delete(`/photos/${entry.existingId}`);
      } catch {
        setSubmitError('Could not remove that photo. Please try again.');
        return;
      }
    }
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  // Apply a cropped/rotated version of a photo. In create mode we swap the
  // local base64; in edit mode the photo is already on the server, so we
  // upload the new version and delete the old (preserving its shot type).
  const replacePhoto = async (index: number, newBase64: string) => {
    const entry = photos[index];
    setCropIndex(null);
    if (isEdit && editId && entry?.existingId) {
      try {
        const res = await api.post<{ id: string }>(`/items/${editId}/photos`, {
          image: newBase64,
          isPrimary: index === 0,
          kind: entry.kind ?? 'item',
        });
        await api.delete(`/photos/${entry.existingId}`);
        setPhotos((prev) =>
          prev.map((p, i) => (i === index ? { base64: newBase64, existingId: res.id, kind: p.kind } : p)),
        );
      } catch {
        setSubmitError('Could not save the adjusted photo. Please try again.');
      }
    } else {
      setPhotos((prev) => prev.map((p, i) => (i === index ? { ...p, base64: newBase64 } : p)));
    }
  };

  const buildPayload = (resolvedStore: {
    storeId: string | null;
    storeName: string | null;
  }): Record<string, unknown> => {
    // Cost is clamped to a non-negative number (no negative cost).
    const costNum = cost ? Math.max(0, Number(cost)) : null;
    const cleanMaterials = materials.map((m) => m.trim()).filter(Boolean);
    return {
      name: name.trim(),
      brand: brand.trim() || null,
      category,
      subcategory: isBags ? subcategory || null : null,
      dimLength: isBags && dimLength ? Number(dimLength) : null,
      dimWidth: isBags && dimWidth ? Number(dimWidth) : null,
      dimHeight: isBags && dimHeight ? Number(dimHeight) : null,
      dimUnit: isBags && (dimLength || dimWidth || dimHeight) ? dimUnit : null,
      seasons,
      cost: costNum,
      currency,
      storeId: resolvedStore.storeId,
      storeName: resolvedStore.storeName,
      acquisitionDate: isWishlist ? null : acquisitionDate || null,
      deliveredDate: status === 'in-closet' ? deliveredDate || null : null,
      wishlistAddedDate: isWishlist ? wishlistAddedDate || null : null,
      materials: cleanMaterials,
      // Size and dimensions are mutually exclusive: bags use dimensions only.
      sizeType: !isBags && !isBespoke && size ? toStoredSizeType(sizeSystem) : null,
      size: !isBags && !isBespoke && size ? formatStoredSize(sizeSystem, size) : null,
      isBespoke: !isBags && isBespoke,
      bespokeDate: !isBags && isBespoke ? bespokeDate || acquisitionDate || null : null,
      color: colors[0]?.name ?? null,
      colorHex: colors[0]?.hex ?? null,
      colors,
      mill: !isBags && isBespoke ? mill.trim() || null : null,
      careInstructions: isWishlist ? [] : careInstructions,
      description: description.trim() || null,
      notes: notes.trim() || null,
      status,
      closetId: status === 'wishlist' ? null : closetId || null,
      onlineStoreUrl: isWishlist ? onlineStoreUrl.trim() || null : null,
      pendingType: status === 'pending-delivery' ? pendingType : null,
      carrier: status === 'pending-delivery' && pendingType === 'being-delivered' ? carrier || null : null,
      trackingNumber:
        status === 'pending-delivery' && pendingType === 'being-delivered' ? trackingNumber || null : null,
      expectedDeliveryDate:
        status === 'pending-delivery' && pendingType === 'being-delivered'
          ? expectedDeliveryDate || null
          : null,
      appointmentDate:
        status === 'pending-delivery' && pendingType !== 'being-delivered' ? appointmentDate || null : null,
      appointmentTime:
        status === 'pending-delivery' && pendingType !== 'being-delivered' ? appointmentTime || null : null,
      appointmentLocation:
        status === 'pending-delivery' && pendingType !== 'being-delivered'
          ? appointmentLocation || null
          : null,
    };
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setSubmitError('Please enter an item name.');
      return;
    }
    if (!category) {
      setSubmitError('Please choose a category.');
      return;
    }
    if (cost && Number(cost) < 0) {
      setSubmitError('Cost cannot be negative.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const resolvedStore = await resolveStore();
      if (isEdit && editId) {
        // Photos are added/removed live in edit mode, so just persist fields.
        await api.patch<{ item: Item }>(`/items/${editId}`, buildPayload(resolvedStore));
        navigate(`/wardrobe/item/${editId}`);
      } else {
        // A data: URL came from the server already (base64) — persist it via the
        // proven base64 photo path. A plain http URL is a display-only fallback
        // the server will try to fetch again.
        const linkIsData = !!linkImage && linkImage.startsWith('data:');
        const payload = {
          ...buildPayload(resolvedStore),
          photos: [
            ...photos.map((p) => ({ image: p.base64, kind: p.kind ?? 'item' })),
            ...(linkIsData ? [{ image: linkImage as string, kind: 'item' as const }] : []),
          ],
          photoUrls: linkImage && !linkIsData ? [linkImage] : [],
        };
        const res = await api.post<{ item: Item }>('/items', payload);
        // Rapid Intake: hop straight back to the camera (with the running tally)
        // to keep adding, instead of landing on the item detail page.
        const handoff = location.state as CameraHandoff | null;
        if (handoff?.rapidIntake) {
          navigate('/camera', {
            state: { rapidIntake: true, intakeCount: (handoff.intakeCount ?? 0) + 1 },
          });
        } else {
          navigate(res?.item?.id ? `/wardrobe/item/${res.item.id}` : '/wardrobe/items');
        }
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save item');
      setSubmitting(false);
    }
  };

  if (editId && editLoading) {
    return (
      <Page>
        <PageHeader section="Edit Item" backHref="/wardrobe" backLabel="Wardrobe" />
        <Main>
          <LoadingState />
        </Main>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        section={isEdit ? 'Edit Item' : 'Add Item'}
        backHref="/wardrobe"
        backLabel="Wardrobe"
      />
      <Main className="space-y-5">
        {editError && <ErrorState message={editError} />}

        {/* Photos */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">Photos</label>
            <button
              type="button"
              onClick={pasteFromClipboardButton}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary text-xs font-medium text-foreground hover:bg-muted transition-colors"
              title="Copy an image anywhere, then paste it here (Ctrl/Cmd+V works too)"
            >
              <ClipboardPaste className="w-3.5 h-3.5" />
              Paste image
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            Tip: right-click an image on any site → Copy Image, then press Ctrl/Cmd+V here.
          </p>
          {pasteHint && <p className="text-xs text-muted-foreground">{pasteHint}</p>}
          <div className="flex gap-3 overflow-x-auto pb-2">
            {photos.map((photo, index) => (
              <div key={index} className="relative flex-shrink-0 w-24">
                <div className="relative w-24 h-24">
                  <div className="w-full h-full rounded-xl bg-secondary border border-border overflow-hidden">
                    <img src={photo.base64} alt="" className="w-full h-full object-cover" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setCropIndex(index)}
                    className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center hover:border-primary/40 transition-colors"
                    aria-label="Adjust photo"
                  >
                    <Crop className="w-3 h-3 text-foreground" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center hover:bg-destructive hover:text-white transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  {index === 0 && (
                    <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-primary text-primary-foreground text-[8px] font-medium rounded">
                      Main
                    </span>
                  )}
                </div>
                {/* Item-shot vs wear-shot toggle */}
                <div className="mt-1 flex rounded-lg overflow-hidden border border-border">
                  {(['item', 'wear'] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setPhotoKind(index, k)}
                      className={cn(
                        'flex-1 py-1 text-[9px] font-medium transition-colors',
                        (photo.kind ?? 'item') === k
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-card text-muted-foreground',
                      )}
                    >
                      {k === 'item' ? 'Item' : 'Wear'}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <label className="flex-shrink-0 w-24 h-24 rounded-xl border-2 border-dashed border-border bg-secondary/50 flex flex-col items-center justify-center gap-1 hover:border-primary/30 hover:bg-secondary transition-all cursor-pointer">
              <Camera className="w-6 h-6 text-muted-foreground" />
              <span className="text-[10px] font-medium text-muted-foreground">Add Photo</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handlePhotoFiles(e.target.files)}
              />
            </label>
            {/* Preview thumbnail sourced from a wishlist link (best-effort). */}
            {isWishlist && linkImage && (
              <div className="relative flex-shrink-0 w-24 h-24">
                <div className="w-full h-full rounded-xl bg-secondary border border-border overflow-hidden">
                  <img src={linkImage} alt="" className="w-full h-full object-cover" />
                </div>
                <button
                  type="button"
                  onClick={() => setLinkImage(null)}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center hover:bg-destructive hover:text-white transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <span
                  className={cn(
                    'absolute bottom-1 left-1 px-1.5 py-0.5 text-[8px] font-medium rounded',
                    linkImage.startsWith('data:')
                      ? 'bg-green-600 text-white'
                      : 'bg-amber-500 text-white',
                  )}
                >
                  {linkImage.startsWith('data:') ? '✓ Will save' : 'Preview only'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Status selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Item Status</label>
          <div className="grid grid-cols-3 gap-2">
            <StatusButton
              active={status === 'in-closet'}
              onClick={() => setStatus('in-closet')}
              icon={<Home className="w-5 h-5" />}
              label="In Closet"
            />
            <StatusButton
              active={status === 'pending-delivery'}
              onClick={() => setStatus('pending-delivery')}
              icon={<Package className="w-5 h-5" />}
              label="Pending"
            />
            <StatusButton
              active={status === 'wishlist'}
              onClick={() => setStatus('wishlist')}
              icon={<ShoppingBag className="w-5 h-5" />}
              label="Wishlist"
              accent
            />
          </div>
        </div>

        {/* Wishlist banner + online store link */}
        {isWishlist && (
          <>
            <div className="flex items-center gap-3 px-4 py-3 bg-accent/10 border border-accent/20 rounded-xl text-sm">
              <Heart className="w-4 h-4 text-accent-foreground" />
              <span className="text-foreground font-medium">Saved to your wishlist</span>
            </div>
            <Field label="Online Store Link">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  {onlineStoreUrl.trim() ? (
                    <a
                      href={/^https?:\/\//i.test(onlineStoreUrl.trim()) ? onlineStoreUrl.trim() : `https://${onlineStoreUrl.trim()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open link"
                      className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-muted-foreground hover:text-primary transition-colors"
                    >
                      <LinkIcon className="w-4 h-4" />
                    </a>
                  ) : (
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  )}
                  <input
                    type="url"
                    value={onlineStoreUrl}
                    onChange={(e) => {
                      setOnlineStoreUrl(e.target.value);
                      setLinkError(false);
                    }}
                    onBlur={() => {
                      if (onlineStoreUrl.trim()) fetchLinkPreview();
                    }}
                    placeholder="https://store.com/product…"
                    className={cn(inputClass, 'pl-10')}
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={fetchLinkPreview}
                  disabled={linkLoading || !onlineStoreUrl.trim()}
                >
                  {linkLoading ? 'Fetching…' : 'Fetch'}
                </Button>
              </div>
              {linkError && (
                <p className="text-xs text-muted-foreground mt-1.5">Couldn't read that link.</p>
              )}
            </Field>
          </>
        )}

        {/* Closet location for owned / pending */}
        {(status === 'in-closet' || status === 'pending-delivery') && (
          <Field label={status === 'pending-delivery' ? 'Expecting Delivery Closet' : 'Closet'}>
            <button
              type="button"
              onClick={() => setShowCloset(true)}
              className={cn(inputClass, 'flex items-center justify-between text-left')}
            >
              <span className={closetId ? 'text-foreground' : 'text-muted-foreground'}>
                {closets.find((c) => c.id === closetId)?.name ?? 'Select closet'}
              </span>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
          </Field>
        )}

        {/* Pending type + fields */}
        {status === 'pending-delivery' && (
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Pending Type</label>
            <div className="grid grid-cols-3 gap-2">
              <StatusButton
                active={pendingType === 'being-delivered'}
                onClick={() => setPendingType('being-delivered')}
                icon={<Truck className="w-4 h-4" />}
                label="Delivery"
                small
              />
              <StatusButton
                active={pendingType === 'pickup'}
                onClick={() => setPendingType('pickup')}
                icon={<StoreIcon className="w-4 h-4" />}
                label="Pick Up"
                small
              />
              <StatusButton
                active={pendingType === 'fitting'}
                onClick={() => setPendingType('fitting')}
                icon={<Ruler className="w-4 h-4" />}
                label="Fitting"
                small
              />
            </div>

            {pendingType === 'being-delivered' ? (
              <div className="space-y-3">
                <Field label="Expected Delivery Date">
                  <input
                    type="date"
                    className={inputClass}
                    value={expectedDeliveryDate}
                    onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Carrier">
                    <input
                      className={inputClass}
                      value={carrier}
                      onChange={(e) => setCarrier(e.target.value)}
                      placeholder="FedEx"
                    />
                  </Field>
                  <Field label="Tracking #">
                    <input
                      className={inputClass}
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                    />
                  </Field>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Date">
                    <input
                      type="date"
                      className={inputClass}
                      value={appointmentDate}
                      onChange={(e) => setAppointmentDate(e.target.value)}
                    />
                  </Field>
                  <Field label="Time">
                    <input
                      type="time"
                      className={inputClass}
                      value={appointmentTime}
                      onChange={(e) => setAppointmentTime(e.target.value)}
                    />
                  </Field>
                </div>
                <Field label="Location">
                  <ComboSelect
                    value={appointmentLocation}
                    options={storeNames}
                    onChange={setAppointmentLocation}
                    placeholder="Select a store"
                    addLabel="+ Add new store…"
                    addPlaceholder="Store name or location"
                  />
                </Field>
              </div>
            )}
          </div>
        )}

        {/* Name */}
        <Field label="Item Name">
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Wish Wool Polo Shirt"
          />
        </Field>

        {/* Brand / Designer — pick existing or add a new one. */}
        <Field label="Designer / Brand">
          <ComboSelect
            value={brand}
            options={designerNames}
            onChange={(v) => {
              setBrand(v);
              // Auto-fill the store from the linked store closest in name,
              // only if no store has been chosen yet.
              if (v && !storeName.trim()) {
                const match = storeForDesigner(v);
                if (match) {
                  setStoreName(match.name);
                  setStoreId(match.id);
                }
              }
            }}
            placeholder="Select a designer"
            addLabel="+ Add new designer…"
            addPlaceholder="e.g., Loro Piana"
          />
        </Field>

        {/* Category — clothing/footwear first, then a divider, then accessories. */}
        <Field label="Category">
          <select
            value={category}
            onChange={(e) => {
              const c = e.target.value;
              setCategory(c);
              if (c !== 'Bags') setSubcategory('');
            }}
            className={cn(inputClass, 'appearance-none')}
          >
            <option value="">Select category</option>
            <optgroup label="Clothing & Footwear">
              {CLOTHING_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </optgroup>
            <optgroup label="Accessories & Bags">
              {ACCESSORY_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </optgroup>
          </select>
        </Field>

        {/* Bag subtype + dimensions */}
        {category === 'Bags' && (
          <>
            <Field label="Bag Type">
              <button
                type="button"
                onClick={() => setShowSubtype(true)}
                className={cn(inputClass, 'flex items-center justify-between text-left')}
              >
                <span className={subcategory ? 'text-foreground' : 'text-muted-foreground'}>
                  {subcategory || 'Select bag type'}
                </span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>
            </Field>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground">Dimensions</label>
                <div className="flex gap-1">
                  {(['cm', 'in'] as const).map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setDimUnit(u)}
                      className={cn(
                        'px-3 py-1 rounded-lg text-xs font-medium',
                        dimUnit === u ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground',
                      )}
                    >
                      {u.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="number"
                  className={cn(inputClass, 'text-center')}
                  value={dimLength}
                  onChange={(e) => setDimLength(e.target.value)}
                  placeholder="L"
                />
                <input
                  type="number"
                  className={cn(inputClass, 'text-center')}
                  value={dimWidth}
                  onChange={(e) => setDimWidth(e.target.value)}
                  placeholder="W"
                />
                <input
                  type="number"
                  className={cn(inputClass, 'text-center')}
                  value={dimHeight}
                  onChange={(e) => setDimHeight(e.target.value)}
                  placeholder="H"
                />
              </div>
            </div>
          </>
        )}

        {/* Seasons */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Season</label>
          <div className="flex gap-2">
            {SEASONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => toggle(s.id as Season, seasons, setSeasons)}
                className={cn(
                  'flex-1 py-2.5 rounded-xl text-sm font-medium transition-all border',
                  seasons.includes(s.id as Season)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card border-border text-muted-foreground hover:border-primary/30',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Cost — small currency selector on the left, wide amount on the right.
            Explicit widths (no inputClass w-full) so the flex sizing holds. */}
        <Field label="Cost">
          <div className="flex gap-2">
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-20 shrink-0 px-2.5 py-2.5 rounded-xl bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Currency"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              type="number"
              min="0"
              step="0.01"
              className="flex-1 min-w-0 px-3.5 py-2.5 rounded-xl bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="0.00"
            />
          </div>
        </Field>

        {/* Store — pick existing or add a new one. */}
        <Field label="Store">
          <ComboSelect
            value={storeName}
            options={storeNames}
            onChange={(v) => {
              setStoreName(v);
              const store = stores.find((s) => s.name === v);
              setStoreId(store?.id ?? '');
              // Auto-fill the designer from the store's closest linked designer,
              // only if no designer has been chosen yet.
              if (store && !brand.trim()) {
                const match = designerForStore(store);
                if (match) setBrand(match);
              }
            }}
            placeholder="Where you bought it"
            addLabel="+ Add new store…"
            addPlaceholder="Store name"
          />
        </Field>

        {/* Acquisition / wishlist date */}
        {isWishlist ? (
          <Field label="Added to Wishlist">
            <input
              type="date"
              className={inputClass}
              value={wishlistAddedDate}
              onChange={(e) => setWishlistAddedDate(e.target.value)}
            />
          </Field>
        ) : (
          <div className={cn(status === 'in-closet' && 'grid grid-cols-1 sm:grid-cols-2 gap-3')}>
            <Field label="Acquisition Date">
              <input
                type="date"
                className={cn(inputClass, 'w-full min-w-0')}
                value={acquisitionDate}
                onChange={(e) => setAcquisitionDate(e.target.value)}
              />
            </Field>
            {/* Pending items capture an expected delivery date in the section
                below; once in-closet, record the actual delivery date here. */}
            {status === 'in-closet' && (
              <Field label="Delivery Date">
                <input
                  type="date"
                  className={cn(inputClass, 'w-full min-w-0')}
                  value={deliveredDate}
                  onChange={(e) => setDeliveredDate(e.target.value)}
                />
              </Field>
            )}
          </div>
        )}

        {/* Materials — one select per chosen material, with "add another". */}
        <Field label="Materials">
          <div className="space-y-2">
            {materials.map((m, i) => (
              <div key={i} className="flex gap-2">
                <select
                  value={m}
                  onChange={(e) => setMaterialAt(i, e.target.value)}
                  className={cn(inputClass, 'appearance-none flex-1')}
                >
                  <option value="">Select material</option>
                  {MATERIALS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {materials.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => moveMaterial(i, -1)}
                      disabled={i === 0}
                      className="flex-shrink-0 w-10 rounded-xl bg-secondary border border-border flex items-center justify-center text-muted-foreground disabled:opacity-40"
                      aria-label="Move material up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveMaterial(i, 1)}
                      disabled={i === materials.length - 1}
                      className="flex-shrink-0 w-10 rounded-xl bg-secondary border border-border flex items-center justify-center text-muted-foreground disabled:opacity-40"
                      aria-label="Move material down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => removeMaterialAt(i)}
                  className="flex-shrink-0 w-10 rounded-xl bg-secondary border border-border flex items-center justify-center text-muted-foreground hover:bg-destructive hover:text-white transition-colors"
                  aria-label="Remove material"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addMaterial}
              className="text-sm font-medium text-primary hover:opacity-80"
            >
              + Add another material
            </button>
            {materials.filter(Boolean).length > 1 && (
              <p className="text-xs text-muted-foreground">The first material is the dominant one.</p>
            )}
          </div>
        </Field>

        {/* Size — hidden for bags (which use dimensions instead). */}
        {!isBags && (
          <SizeField
            category={category}
            system={sizeSystem}
            size={size}
            isBespoke={isBespoke}
            bespokeDate={bespokeDate}
            acquisitionDate={acquisitionDate}
            onSystemChange={setSizeSystem}
            onSizeChange={setSize}
            onBespokeChange={setIsBespoke}
            onBespokeDateChange={setBespokeDate}
          />
        )}

        {/* Mill / cloth — bespoke only */}
        {!isBags && isBespoke && (
          <Field label="Mill / Cloth">
            <input
              type="text"
              value={mill}
              onChange={(e) => setMill(e.target.value)}
              placeholder="e.g. Vitale Barberis Canonico"
              className={inputClass}
            />
          </Field>
        )}

        {/* Colors — ordered; the first is the dominant color. */}
        <Field label="Colors">
          <div className="space-y-2">
            {colors.map((c, i) => (
              <div key={c.name} className="flex items-center gap-2">
                <span
                  className="w-5 h-5 rounded-full border border-border flex-shrink-0"
                  style={{ backgroundColor: c.hex }}
                />
                <span className="text-sm text-foreground flex-1 truncate">{c.name}</span>
                {i === 0 && colors.length > 1 && (
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground flex-shrink-0">
                    Dominant
                  </span>
                )}
                {colors.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => moveColor(i, -1)}
                      disabled={i === 0}
                      className="flex-shrink-0 w-9 h-9 rounded-lg bg-secondary border border-border flex items-center justify-center text-muted-foreground disabled:opacity-40"
                      aria-label="Move color up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveColor(i, 1)}
                      disabled={i === colors.length - 1}
                      className="flex-shrink-0 w-9 h-9 rounded-lg bg-secondary border border-border flex items-center justify-center text-muted-foreground disabled:opacity-40"
                      aria-label="Move color down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => removeColorAt(i)}
                  className="flex-shrink-0 w-9 h-9 rounded-lg bg-secondary border border-border flex items-center justify-center text-muted-foreground hover:bg-destructive hover:text-white transition-colors"
                  aria-label="Remove color"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setShowColor(true)}
              className="text-sm font-medium text-primary hover:opacity-80"
            >
              + Add color
            </button>
          </div>
        </Field>

        {/* Care instructions (not for wishlist) */}
        {!isWishlist && (
          <Field label="Care Instructions">
            <button
              type="button"
              onClick={() => setShowCare(true)}
              className={cn(inputClass, 'flex items-center justify-between text-left min-h-[44px]')}
            >
              <span className="flex items-center gap-1.5 flex-wrap flex-1">
                {careInstructions.length ? (
                  <>
                    {careInstructions.slice(0, 2).map((c) => (
                      <span key={c} className="px-2 py-0.5 bg-secondary rounded-full text-xs text-foreground">
                        {c}
                      </span>
                    ))}
                    {careInstructions.length > 2 && (
                      <span className="text-xs text-muted-foreground">
                        +{careInstructions.length - 2} more
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground">Select care instructions</span>
                )}
              </span>
              <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0 ml-2" />
            </button>
          </Field>
        )}

        {/* Description */}
        <Field label="Description">
          <textarea
            className={inputClass}
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the item…"
          />
        </Field>

        {/* Notes */}
        <Field label="Notes">
          <textarea
            className={inputClass}
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes…"
          />
        </Field>

        {submitError && <ErrorState message={submitError} />}

        <Button fullWidth onClick={handleSubmit} disabled={submitting}>
          {submitting
            ? 'Saving…'
            : isEdit
              ? 'Save Changes'
              : isWishlist
                ? 'Add to Wishlist'
                : status === 'pending-delivery'
                  ? 'Add Pending Item'
                  : 'Add to Wardrobe'}
        </Button>
      </Main>

      {/* Pickers */}
      <PickerSheet isOpen={showSubtype} onClose={() => setShowSubtype(false)} title="Select Bag Type">
        {BAG_SUBTYPES.map((s) => (
          <PickerRow
            key={s}
            label={s}
            selected={subcategory === s}
            onClick={() => {
              setSubcategory(s);
              setShowSubtype(false);
            }}
          />
        ))}
      </PickerSheet>

      <PickerSheet isOpen={showColor} onClose={() => setShowColor(false)} title="Add Color">
        <div className="grid grid-cols-4 gap-3 p-2">
          {COLORS.map((c) => {
            const chosen = colors.some((x) => x.name === c.name);
            return (
              <button
                key={c.name}
                type="button"
                disabled={chosen}
                onClick={() => {
                  addColor({ name: c.name, hex: c.hex });
                  setShowColor(false);
                }}
                className={cn(
                  'flex flex-col items-center gap-2 p-3 rounded-xl transition-all',
                  chosen ? 'bg-primary/10 ring-2 ring-primary opacity-60' : 'hover:bg-secondary',
                )}
              >
                <span
                  className="w-10 h-10 rounded-full border border-border"
                  style={{ backgroundColor: c.hex }}
                />
                <span className="text-[10px] font-medium text-foreground text-center leading-tight">
                  {c.name}
                </span>
              </button>
            );
          })}
        </div>
      </PickerSheet>

      <PickerSheet
        isOpen={showCare}
        onClose={() => setShowCare(false)}
        title="Care Instructions"
        footer={
          <Button fullWidth onClick={() => setShowCare(false)}>
            Done ({careInstructions.length})
          </Button>
        }
      >
        {CARE_INSTRUCTIONS.map((c) => (
          <PickerRow
            key={c}
            label={c}
            selected={careInstructions.includes(c)}
            onClick={() => toggle(c, careInstructions, setCareInstructions)}
          />
        ))}
      </PickerSheet>

      <PickerSheet isOpen={showCloset} onClose={() => setShowCloset(false)} title="Select Closet">
        {closets.map((c) => (
          <PickerRow
            key={c.id}
            label={c.name}
            sublabel={c.locationText ?? undefined}
            selected={closetId === c.id}
            onClick={() => {
              setClosetId(c.id);
              setShowCloset(false);
            }}
          />
        ))}
      </PickerSheet>

      {cropIndex !== null && photos[cropIndex] && (
        <PhotoCropper
          image={photos[cropIndex].base64}
          onCancel={() => setCropIndex(null)}
          onApply={(b64) => replacePhoto(cropIndex, b64)}
        />
      )}
    </Page>
  );
}

function StatusButton({
  active,
  onClick,
  icon,
  label,
  accent,
  small,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  accent?: boolean;
  small?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-xl border transition-all',
        small ? 'p-3' : 'p-3',
        active
          ? accent
            ? 'bg-accent text-accent-foreground border-accent'
            : 'bg-primary text-primary-foreground border-primary'
          : 'bg-card border-border text-foreground hover:border-primary/30',
      )}
    >
      {icon}
      <span className={cn('font-medium', small ? 'text-[10px]' : 'text-xs')}>{label}</span>
    </button>
  );
}
